import PageHead from '@/components/PageHead';
import PageLayout from '@/components/layout/page-layout';
import Container from '@/components/Container';
import Timeline from '@/components/TimelineHistoriaInstituto';
import HeadingOne from '@/components/Heading/HeadingOne';
import HorizontalRow from '@/components/HorizontalRow';

import NextImage from 'next/image';
import NextLink from 'next/link';

import { IoMdHeartEmpty } from 'react-icons/io';
import { MdArrowDownward } from 'react-icons/md';
import { ExtendProps } from 'app';
import { HTMLAttributes } from 'react';

type OsdFigure = HTMLAttributes<HTMLElement> & {
	image: {
		src: string;
		label: string;
		caption: string;
	};
};
function OdsFigure(props: ExtendProps<OsdFigure>) {
	const { className } = props;

	return (
		<figure
			className={`w-[100%] flex items-center gap-4 lg:flex-col relative ${
				className ? className : ''
			}`}
		>
			<picture tabIndex={0}>
				<source srcSet={props.image.src} media="(min-width: 0)" />
				<NextImage
					className="min-w-[144px] h-auto"
					src={props.image.src}
					alt={props.image.label}
					aria-label={props.image.label}
					width={144}
					height={144}
				/>
			</picture>
			<figcaption className="font-bold lg:text-center">
				{props.image.caption}
			</figcaption>
		</figure>
	);
}

export default function Page() {
	return (
		<>
			<PageHead
				page={{
					title: 'Instituto Deixe Vivo | Por que existimos?',
					keywords: 'OSC, Instituto, Deixe Vivo, Doação de órgãos e tecidos, transplante, transplantes, tecidos, família doadora',
					colorScheme: 'light dark',
					description: 'Para informar toda a sociedade sobre a importância de se declarar doador(a) de órgãos e tecidos',
					robots: 'follow, index',
				}}
				og={{
					description: 'Para informar toda a sociedade sobre a importância de se declarar doador(a) de órgãos e tecidos',
					site_name: 'Instituto Deixe Vivo | Por que existimos?',
					title: 'Instituto Deixe Vivo | Por que existimos?',
					type: 'website',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						fbAdmins: 'instituto.deixevivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
						type: 'image/jpg',
						width: '1200',
						height: '1200',
					},
				}}
				twitter={{
					card: 'summary',
					description: 'Para informar toda a sociedade sobre a importância de se declarar doador(a) de órgãos e tecidos',
					site: 'Instituto Deixe Vivo | Por que existimos?',
					title: 'Instituto Deixe Vivo | Por que existimos?',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
					},
				}}
			/>
			<PageLayout>
				<Container className="py-8">
					<HeadingOne horizontalRow>Por que existimos?</HeadingOne>

					<div className="mt-8">
						<section>
							<strong tabIndex={0}>
								Por que precisa existir um Instituto voltado para a doação de
								órgãos?
							</strong>

							<p className="mt-2">
								Todos nós temos direito à vida. Mas e quando essa vida precisa
								ser renovada? E quando alguém precisa de uma nova chance para
								continuar vivendo?
							</p>

							<p className="mt-2">
								Atualmente, no Brasil, quase 60 mil pessoas estão na espera por
								um transplante e é por meio da doação de órgãos que todas essas
								vidas podem ser salvas.
							</p>

							<p className="mt-2">
								São pessoas cheias de sonhos e com algo em comum: todas
								aguardando uma nova chance digna de viver, todas à espera do
								“SIM” para a doação de órgãos. O mais incrível é que todos nós
								podemos contribuir com a causa, despertando a cultura doadora no
								Brasil. Como? Simples! Declarando-se doador(a) de órgãos e
								avisando nossas famílias sobre a nossa decisão.
							</p>

							<p className="mt-2">
								Segundo dados do Ministério da Saúde, a recusa para a doação de
								órgãos no Brasil é de mais de 44%. São pessoas e famílias que
								não concordam com a doação, geralmente por não saberem como
								funciona o processo como um todo e por não entenderem o que
								significa uma pessoa, após a morte, ser candidata à doação. Além
								de, provavelmente, não terem a menor ideia de quantas vidas
								podem ser salvas a partir desse ato de amor e empatia ao próximo
								e que a chance de uma pessoa precisar de um transplante de órgão
								é maior do que a possibilidade dela se tornar doadora.
							</p>

							<div className="flex flex-col items-center w-[100%] lg:w-[60%] xl:w-[40%] mx-auto gap-4 my-8">
								<p className="bg-primary-dark p-4 rounded-md w-[100%] text-center text-white">
									Durante o ano de 2022, houve 13.195 notificações de potenciais
									doadores de órgãos.
								</p>
								<MdArrowDownward className="text-2xl text-primary-dark" />
								<p className="bg-primary-dark p-4 rounded-md w-[100%] text-center text-white">
									Desse total de notificações, foram realizadas 7.598
									entrevistas com as famílias dos potenciais doadores de órgãos.
								</p>
								<MdArrowDownward className="text-2xl text-primary-dark" />
								<p className="bg-primary-dark p-4 rounded-md w-[100%] text-center text-white">
									Dessas entrevistas, 3.523 famílias negaram a doação de órgãos.
									Isto é, 46% deixaram de salvar outras vidas.
								</p>

								<em className="text-sm font-bold text-right">
									<abbr
										title="Associação Brasileiro de Transplante de
									Órgãos"
									>
										ABTO
									</abbr>{' '}
									(Associação Brasileira de Transplante de Órgãos) / RBT 2022
									(nº 4) Dimensionamento dos transplantes no Brasil em cada
									estado (página 26)
								</em>
							</div>

							<p className="mt-2">
								O Instituto Deixe Vivo trabalha para que as pessoas que
								necessitam de um transplante de órgão, fiquem cada vez menos
								tempo na lista de espera. Para isso, facilitamos por meio da
								informação e da educação a importância da doação de órgãos.
								Queremos preencher o vazio entre quem precisa de um novo órgão e
								quem pode doar e o vazio da falta de conhecimento sobre o tema.
								Acreditamos que a informação junto à educação sejam a chave para
								transformar pessoas, que por sua vez, transformarão o mundo com
								seu conhecimento, atitude e postura.
							</p>

							<p className="mt-2">
								Com nossas ações, programas, serviços e campanhas, fomentamos e
								debatemos o tema “Educar para Despertar” dentro de diversos
								ambientes (escolas, empresas, eventos). E, por meio dos projetos
								do Instituto, viabilizamos às pessoas em tratamento, pessoas que
								aguardam em lista por um transplante, pacientes já
								transplantados e famílias doadoras: voz, acolhimento e
								representatividade.
							</p>

							<p className="mt-2">
								Quanto mais pessoas informadas, quanto mais pessoas falando
								sobre doação de órgãos de forma correta, temos indiretamente - e
								teremos cada vez mais - um aumento do número de doadores que
								reflete na diminuição de pessoas esperando por um transplante e
								no tempo de espera por ele.
							</p>

							<p className="mt-2">
								É um trabalho em equipe, resultado do despertar da cultura da
								doação de órgãos no Brasil.
							</p>
						</section>
					</div>
				</Container>
			</PageLayout>
		</>
	);
}
